import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import React, { ChangeEventHandler, forwardRef, useCallback } from 'react';
import { useIntl } from 'react-intl';

import {
  ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo,
  ReservationFlowSelectedPaymentMethodNewCreditCard,
} from 'src/interfaces/reservation_flow';
import { RSCheckBoxWithLabel } from 'src/reservation-page/RSCheckBoxWithLabel';
import { PaymentElementForm } from 'src/templates/merchants/purchase/components/PaymentElementForm';

import {
  CreditCardInfoInput,
  CreditCardInfoInputProps,
} from 'src/templates/merchants/resources/book/components/CreditCardInfoInput';

export interface PaymentMethodInputNewCreditCardProps {
  value?:
    | ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo
    | ReservationFlowSelectedPaymentMethodNewCreditCard;
  onChange: (
    paymentMethod: ReservationFlowSelectedPaymentMethodNewCreditCard,
  ) => void;
  onBlur: () => void;
  stripeError?: stripe.StripeError;
  currentYear: string;
  isJCBEnabled: boolean;
  isMonthlyBilling: boolean;
  isLegacyForm: boolean;
  handlePaymentElementChange: (event: StripePaymentElementChangeEvent) => void;
}

export const PaymentMethodInputNewCreditCard = forwardRef<
  HTMLDivElement,
  PaymentMethodInputNewCreditCardProps
>(
  (
    {
      value,
      onChange,
      stripeError,
      currentYear,
      isJCBEnabled,
      isMonthlyBilling,
      isLegacyForm,
      handlePaymentElementChange,
    },
    ref,
  ) => {
    const intl = useIntl();
    const onCreditCardInputChange = useCallback<
      CreditCardInfoInputProps['onChange']
    >(
      (creditCardInfo) => {
        if (typeof value === 'undefined' || value.type !== 'new_credit_card') {
          onChange({
            type: 'new_credit_card',
            creditCardInfo,
            isStoreCreditCard: false,
          });
          return;
        }

        onChange({ ...value, creditCardInfo });
      },
      [onChange, value],
    );

    const onIsStoreCreditCardInputChange = useCallback<
      ChangeEventHandler<HTMLInputElement>
    >(
      (event) => {
        if (isMonthlyBilling) return;

        if (typeof value === 'undefined' || value.type !== 'new_credit_card') {
          onChange({
            type: 'new_credit_card',
            creditCardInfo: {},
            isStoreCreditCard: event.target.checked,
          });
          return;
        }

        onChange({ ...value, isStoreCreditCard: event.target.checked });
      },
      [onChange, value, isMonthlyBilling],
    );

    return (
      <div ref={ref}>
        {isLegacyForm ? (
          <CreditCardInfoInput
            defaultValues={(() => {
              if (typeof value === 'undefined') {
                return undefined;
              }

              if (value.type !== 'new_credit_card') {
                return undefined;
              }

              return value.creditCardInfo;
            })()}
            onChange={onCreditCardInputChange}
            stripeError={stripeError}
            currentYear={currentYear}
            isJCBEnabled={isJCBEnabled}
          />
        ) : (
          <PaymentElementForm
            onPaymentElementChange={handlePaymentElementChange}
          />
        )}

        <div className="mt-2">
          {(() => {
            if (isMonthlyBilling) {
              return (
                <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                  {intl.formatMessage({
                    defaultMessage:
                      '毎月支払いが発生するため、カード情報は自動的に保存されます。',
                    id: 'C2WOIx',
                    description:
                      'src/organisms/product-purchase/PaymentMethodInput/components/NewCreditCardItem/index.tsx',
                  })}
                </div>
              );
            }

            return (
              <RSCheckBoxWithLabel
                labelText={intl.formatMessage({
                  defaultMessage: 'カード情報を保存する',
                  id: 'ELaVTw',
                  description:
                    'src/organisms/product-purchase/PaymentMethodInput/components/NewCreditCardItem/index.tsx',
                })}
                checked={(() => {
                  if (typeof value === 'undefined') {
                    return false;
                  }

                  if (value.type !== 'new_credit_card') {
                    return false;
                  }

                  return value.isStoreCreditCard;
                })()}
                onChange={onIsStoreCreditCardInputChange}
              />
            );
          })()}
        </div>
      </div>
    );
  },
);

PaymentMethodInputNewCreditCard.displayName = 'PaymentMethodInputNewCreditCard';
